import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import LanguageSwitcher from "./LanguageSwitcher";
import { useSiteSettings } from "@/site/SiteSettingsContext";
import LoginModal from "./LoginModal";
import RegisterModal from "./RegisterModal";
import ForgotPasswordModal from "./ForgotPasswordModal";
import { useDarkMode } from "@/site/DarkModeContext";

export default function Navbar() {
  const { t, i18n } = useTranslation("common");
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const isAccount = location.pathname.startsWith("/account");
  const [mobileOpen, setMobileOpen] = useState(false);
  const { settings } = useSiteSettings();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const logoUrl = settings?.logoUrl?.trim();
  const siteName = settings?.siteName?.trim() || t("appName");
  const isRtl = i18n.dir() === "rtl";

  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem("parent_token"));
  const [parentName, setParentName] = useState(() => localStorage.getItem("parent_name") || "");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  useEffect(() => {
    setIsLoggedIn(!!localStorage.getItem("parent_token"));
    setParentName(localStorage.getItem("parent_name") || "");
  }, [location.pathname, showLogin, showRegister]);

  const handleLogout = () => {
    localStorage.removeItem("parent_token");
    localStorage.removeItem("parent_name");
    setIsLoggedIn(false);
    navigate("/");
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md border-b border-gray-100 dark:border-zinc-900">
        <div className="w-full px-4 md:px-8 py-3 md:py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 md:gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt={siteName} className="w-8 h-8 md:w-10 md:h-10 object-contain" />
          ) : (
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-primary-400 flex items-center justify-center">
              <i className="ri-seedling-line text-white text-base md:text-lg" />
            </div>
          )}
          <span className="text-base md:text-lg font-semibold text-navy-400 dark:text-zinc-100 whitespace-nowrap">
            {siteName}
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-4">
          {!isHome && (
            <Link
              to="/"
              className="flex items-center gap-2 text-sm text-gray-600 dark:text-zinc-400 hover:text-navy-400 dark:hover:text-teal-400 transition-colors whitespace-nowrap"
            >
              <i className={isRtl ? "ri-arrow-right-line" : "ri-arrow-left-line"} />
              {t("back")}
            </Link>
          )}

          {/* Account Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className={`flex items-center gap-2 text-sm px-3 py-2 rounded-xl transition-colors whitespace-nowrap ${
                isAccount
                  ? "bg-primary-400/10 text-primary-500 font-semibold"
                  : "text-gray-600 dark:text-zinc-400 hover:text-navy-400 dark:hover:text-zinc-200 hover:bg-gray-100 dark:hover:bg-zinc-800"
              }`}
            >
              <i className="ri-user-smile-line text-base" />
              <span>{isLoggedIn ? (parentName || t("myAccount")) : t("myAccount")}</span>
              <i className="ri-arrow-down-s-line text-xs opacity-70" />
            </button>

            {showDropdown && (
              <div
                className={`absolute top-full mt-1 ${
                  isRtl ? "left-0" : "right-0"
                } w-48 bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-xl shadow-lg py-2 z-50 animate-fade-in`}
                onMouseLeave={() => setShowDropdown(false)}
              >
                {isLoggedIn ? (
                  <>
                    <Link
                      to="/account"
                      onClick={() => setShowDropdown(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors text-start"
                    >
                      <i className="ri-profile-line" />
                      <span>{t("personalAccount")}</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors text-start"
                    >
                      <i className="ri-logout-box-line" />
                      <span>{t("logout")}</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        setShowLogin(true);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors text-start"
                    >
                      <i className="ri-login-box-line" />
                      <span>{t("login")}</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        setShowRegister(true);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors text-start"
                    >
                      <i className="ri-user-add-line" />
                      <span>{t("register")}</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Dark Mode Switch */}
          <button
            onClick={toggleDarkMode}
            className="w-10 h-10 rounded-xl bg-gray-50 dark:bg-zinc-800 text-gray-700 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-all flex items-center justify-center border border-gray-100 dark:border-zinc-700 shadow-sm"
            title={t("toggleTheme")}
          >
            {isDarkMode ? (
              <i className="ri-sun-fill text-lg text-amber-500" />
            ) : (
              <i className="ri-moon-fill text-lg text-navy-400 dark:text-zinc-400" />
            )}
          </button>

          <LanguageSwitcher />
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileOpen(true)}
          className="md:hidden w-10 h-10 rounded-lg flex items-center justify-center hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-700 dark:text-zinc-300 transition-colors"
        >
          <i className="ri-menu-line text-xl" />
        </button>
        </div>
      </nav>

      {/* Mobile Backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] md:hidden animate-fade-in"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Drawer Menu */}
      {mobileOpen && (
        <div
          className={`fixed top-0 bottom-0 ${
            isRtl ? "right-0 animate-slide-in-right" : "left-0 animate-slide-in-left"
          } w-[280px] h-full bg-white dark:bg-zinc-900 shadow-2xl z-[101] flex flex-col p-6 md:hidden`}
        >
          {/* Drawer Header */}
          <div className="flex items-center justify-between pb-6 border-b border-gray-100 dark:border-zinc-850 mb-6">
            <Link to="/" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
              {logoUrl ? (
                <img src={logoUrl} alt={siteName} className="w-8 h-8 object-contain" />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-primary-400 flex items-center justify-center">
                  <i className="ri-seedling-line text-white text-base" />
                </div>
              )}
              <span className="text-base font-semibold text-navy-400 dark:text-zinc-100 truncate">
                {siteName}
              </span>
            </Link>
            <button
              onClick={() => setMobileOpen(false)}
              className="w-8 h-8 rounded-full bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 flex items-center justify-center transition-colors"
            >
              <i className="ri-close-line text-gray-600 dark:text-zinc-400 text-lg" />
            </button>
          </div>

          {/* Drawer Links */}
          <div className="flex-1 flex flex-col gap-3">
            {!isHome && (
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 text-base font-medium text-gray-700 dark:text-zinc-300 py-2.5 px-3 rounded-xl hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-colors"
              >
                <i className={`text-xl ${isRtl ? "ri-arrow-right-line" : "ri-arrow-left-line"}`} />
                <span>{t("back")}</span>
              </Link>
            )}

            <Link
              to={isLoggedIn ? "/account" : "#"}
              onClick={(e) => {
                setMobileOpen(false);
                if (!isLoggedIn) {
                  e.preventDefault();
                  setShowLogin(true);
                }
              }}
              className={`flex items-center gap-3 text-base font-medium py-2.5 px-3 rounded-xl transition-colors ${
                isAccount
                  ? "bg-primary-400/10 text-primary-500 font-semibold"
                  : "text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-zinc-800/50"
              }`}
            >
              <i className="ri-user-smile-line text-xl" />
              <span>{isLoggedIn ? (parentName || t("myAccount")) : t("myAccount")}</span>
            </Link>

            {isLoggedIn ? (
              <button
                onClick={() => {
                  setMobileOpen(false);
                  handleLogout();
                }}
                className="w-full flex items-center gap-3 text-base font-medium py-2.5 px-3 rounded-xl text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors text-start"
              >
                <i className="ri-logout-box-line text-xl" />
                <span>{t("logout")}</span>
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    setShowLogin(true);
                  }}
                  className="w-full flex items-center gap-3 text-base font-medium py-2.5 px-3 rounded-xl text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-colors text-start"
                >
                  <i className="ri-login-box-line text-xl" />
                  <span>{t("login")}</span>
                </button>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    setShowRegister(true);
                  }}
                  className="w-full flex items-center gap-3 text-base font-medium py-2.5 px-3 rounded-xl text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-colors text-start"
                >
                  <i className="ri-user-add-line text-xl" />
                  <span>{t("register")}</span>
                </button>
              </>
            )}
          </div>

          {/* Drawer Footer (Language & Theme Switcher) */}
          <div className="pt-6 border-t border-gray-100 dark:border-zinc-800 flex flex-col gap-4 mt-auto">
            <div>
              <p className="text-xs text-gray-400 dark:text-zinc-500 font-bold mb-3 uppercase tracking-wider">
                {t("languageAndTheme")}
              </p>
              <div className="flex items-center justify-between gap-2">
                <LanguageSwitcher alignUp={true} />
                <button
                  onClick={toggleDarkMode}
                  className="w-10 h-10 rounded-xl bg-gray-50 dark:bg-zinc-800 text-gray-700 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-all flex items-center justify-center border border-gray-100 dark:border-zinc-700 shadow-sm"
                  title={t("toggleTheme")}
                >
                  {isDarkMode ? (
                    <i className="ri-sun-fill text-lg text-amber-500" />
                  ) : (
                    <i className="ri-moon-fill text-lg text-navy-400 dark:text-zinc-400" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modals */}
      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSuccess={() => setIsLoggedIn(true)}
        onSwitchToRegister={() => {
          setShowLogin(false);
          setShowRegister(true);
        }}
        onForgotPassword={() => {
          setShowLogin(false);
          setShowForgotPassword(true);
        }}
      />
      <RegisterModal
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        onSuccess={() => setIsLoggedIn(true)}
        onSwitchToLogin={() => {
          setShowRegister(false);
          setShowLogin(true);
        }}
      />
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onSwitchToLogin={() => {
          setShowForgotPassword(false);
          setShowLogin(true);
        }}
      />
    </>
  );
}
